<template>
  <div v-click-outside="hideAutoComplete">
    <div class="basic-number-input-container">
      <Field
        v-slot="{ field }"
        v-model="mutatedValue"
        validateOnInput
        :label="label"
        :name="name"
        :rules="validation"
        @update:model-value="onInput"
      >
        <label class="bordered-bottom">
          <span v-if="displayLabel">{{ label }} {{ validation.split('|').includes('required') ? '*' : '' }}</span>
          <input
            :ref="name"
            type="number"
            :model-value="field.value"
            :min="min"
            :max="max"
            v-bind="field"
            :placeholder="`${placeholder} ${!displayLabel && validation.split('|').includes('required') ? '*' : ''}`"
            :autocomplete="autocompletePropsValue"
            @keyup="autocompleteOnKeyUp"
          >
          <img
            v-if="![null, undefined, ''].includes(mutatedValue)"
            class="form-generator-clear-button"
            src="@/assets/icons/cancel.svg"
            alt="clear input"
            @click="clearInput"
          >
        </label>
        <ErrorMessage
          class="error-msg"
          :name="name"
        />
      </Field>
    </div>
    <div
      v-if="autocomplete_.list && autocomplete_.active"
      id="autocompleteList"
    >
      <ul @blur="autocomplete_.active = false">
        <li
          v-for="item in autocomplete_.list"
          :key="item.id"
          @click="selectAutocomplete(item)"
        >
          {{ item }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import vClickOutside from 'click-outside-vue3'
import { autoCompleteService } from '@/services/autocomplete.service'
import { Field, ErrorMessage } from 'vee-validate'
import _ from 'lodash'
import { EventBus } from '@/event-bus.js'

/**
 * Props:
 * value: on load value (Number),
 * name: input's name (String),
 * min: minimum of the input ([Number, String]),
 * max: maximum of the input ([Number, String])
 */
export default {
  name: 'BasicNumberInput',
  components: {
    Field,
    ErrorMessage
  },
  expose: ['hideAutoComplete', 'clearInput'],
  directives: {
    clickOutside: vClickOutside.directive
  },
  props: {
    label: {
      type: String,
      default: ''
    },
    validation: {
      type: [String, Boolean],
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    defaultValue: {
      type: [Number, String, Array],
      default: null
    },
    name: {
      type: String,
      default: ''
    },
    min: {
      type: [String, Number],
      default: null
    },
    max: {
      type: [String, Number],
      default: null
    },
    getChildByName: {
      type: Function,
      default: null
    },
    displayLabel: {
      type: Boolean,
      default: false
    },
    setAutocomplete: {
      type: Function,
      default: null
    },
    autocomplete: {
      type: Object,
      default: function () {
        return {
          enable: false,
          type: null,
          field: null,
          min_length: 3,
          active: false,
          list: []
        }
      }
    }
  },
  emits: ['addData', 'value'],
  data() {
    return {
      mutatedValue: this.defaultValue,
      autocomplete_: {},
      autocompletePropsValue: 'off'
    }
  },
  created() {
    let chrome = /chrom(e|ium)/.test(navigator.userAgent.toLowerCase())
    if(chrome) this.autocompletePropsValue = 'NoAutocompletion'
  },
  mounted() {
    this.validate()
    this.autocomplete_ = this.autocomplete
    //Disable autocomplete for datacapture
    this.autocomplete_.enable = false

    EventBus.on('changeAutocomplete' + this.name, values => {
      this.autocomplete_ = {
        enable: false,
        type: null,
        field: null,
        min_length: 3,
        active: true,
        list: values
      }
    })
  },
  methods: {
    onInput(value = null) {
      this.$emit('value', value)
      this.validate()
    },
    validate() {
      this.mutatedValue
        ? this.$emit('addData', { value: this.mutatedValue, name: this.name })
        : this.$emit('addData', { value: '', name: this.name })
    },
    /* event for autocomplete field */
    autocompleteOnKeyUp: _.debounce(function () {
      /* if autocomplete enabled on field */
      if (this.autocomplete_.enable && this.mutatedValue) {
        /* check if field length is ok for launch autocomplete */
        if (
          this.mutatedValue &&
          this.mutatedValue.length < Math.min(this.autocomplete.min_length, parseInt(this.mutatedMaxLength))
        ) {
          this.autocomplete_['list'] = []
          this.autocomplete_.active = false
          return
        }
        let cityInput = this.getChildByName('city')
        let postalCodeInput = this.getChildByName('postcode')
        let distribution = this.getChildByName('distribution')
        let countryInput = this.getChildByName('country') || this.getChildByName('id_country')
        switch (this.autocomplete.type) {
          case 'address':
            if (cityInput || postalCodeInput) {
              autoCompleteService
                .autoCompleteStreet(
                  this.mutatedValue,
                  cityInput ?? '',
                  postalCodeInput ?? '',
                  distribution ?? '',
                  countryInput
                )
                .then(response => {
                  if (response.length > 0) {
                    if (this.autocomplete.field === this.name) {
                      this.autocomplete_.active = true
                      this.autocomplete_['list'] = response
                    } else {
                      this.autocomplete_.active = false
                      this.autocomplete_['list'] = []
                    }
                  }
                })
                .catch(() => {
                  this.autocomplete_['list'] = []
                  this.autocomplete_.active = false
                })
            }
            break
          case 'postalcode':
            autoCompleteService
              .autoCompletePostalCode(this.mutatedValue, countryInput)
              .then(response => {
                if (response.length > 0) {
                  /* Call parent to set list in other field */
                  const cities = response.map(x => x.city)
                  if (response[0].region) {
                    const regions = response.map(x => x.region)
                    this.setAutocomplete(regions, 'region')
                  }
                  this.setAutocomplete(cities, 'city')
                } else {
                  this.setAutocomplete(false, this.autocomplete.field)
                  this.autocomplete_.active = false
                  this.autocomplete_['list'] = []
                }
              })
              .catch(() => {
                this.autocomplete_.active = false
                this.autocomplete_['list'] = []
              })
            break
          case 'city':
            autoCompleteService
              .autoCompleteCity(this.mutatedValue, countryInput)
              .then(response => {
                if (response.length > 0) {
                  /* Call parent to set list in other field */
                  this.setAutocomplete(response, this.autocomplete.field)
                } else {
                  this.setAutocomplete(false, this.autocomplete.field)
                  this.autocomplete_.active = false
                  this.autocomplete_['list'] = []
                }
              })
              .catch(() => {
                this.autocomplete_.active = false
                this.autocomplete_['list'] = []
              })
            break
          default:
        }
      }
    }, 500),
    selectAutocomplete(item) {
      this.mutatedValue = item
      this.$emit('addData', { value: item, name: this.name })
      this.autocomplete_.active = false
    },
    hideAutoComplete(e) {
      if (e.srcElement.className !== 'autocomplete-item' && this.autocomplete_.active == true) {
        let countryInput = this.getChildByName('country') || this.getChildByName('id_country') || 'FR'
        if(this.autocomplete_.active && countryInput == 'FR' && (this.name == 'postcode' || this.name == 'postalcode')) {
          EventBus.emit('changeValue' + 'city', '')
          EventBus.emit('changeValue' + 'postcode', '')
          EventBus.emit('changeValue' + 'postalcode', '')
          EventBus.emit('changeValue' + 'region', '')
        }
        this.autocomplete_.active = false
      }
    },
    clearInput() {
      this.mutatedValue = ''
      if(!this.disabledInteractionComputed) {
        if(this.valueHasBeenAutocompleted) {
          if(this.name == 'city') {
            EventBus.emit('changeValue' + 'postcode', '')
            EventBus.emit('changeValue' + 'postalcode', '')
            EventBus.emit('changeValue' + 'region', '')
            EventBus.emit('changeValue' + 'line1', '')
            EventBus.emit('changeValue' + 'line2', '')
            EventBus.emit('changeValue' + 'line3', '')
            EventBus.emit('changeValue' + 'distribution', '')
          } else if (this.name == 'postcode' || this.name == 'postalcode') {
            EventBus.emit('changeValue' + 'city', '')
            EventBus.emit('changeValue' + 'region', '')
            EventBus.emit('changeValue' + 'line1', '')
            EventBus.emit('changeValue' + 'line2', '')
            EventBus.emit('changeValue' + 'line3', '')
            EventBus.emit('changeValue' + 'distribution', '')
          } 
        }
        this.valueHasBeenAutocompleted = false
      }
      this.onInput()
    }
  }
}
</script>

<style lang="scss">
  .basic-number-input-container {
    margin: 10px 0;

    label {
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      white-space: nowrap;
      align-items: center;
      padding-left: 0.5rem;

      span {
        font-weight: bolder;
        margin-right: 1rem;
      }

      input {
        border: none;
        flex-grow: 1;
      }
    }

    /* Tablet (portrait) */
    @media only screen and (width >= 768px) {
      margin: 20px 0;
    }

    /* Desktops and laptops */
    @media only screen and (width >= 1024px) {
      margin: 20px 0;
    }

    /* Large screens */
    @media only screen and (width >= 1824px) {
      margin: 20px 0;
    }
  }
</style>
